import React, { useEffect, useRef, useState } from "react";
import micOffIcon from "../assets/mic-off.svg";
import micOnIcon from "../assets/mic-on.svg";
import callIcon from "../assets/call.svg";
import callDropIcon from "../assets/call-end.svg";
import BotProfile from "../assets/bot-profile.png";
// import UserProfile from "../assets/user-profile.png";
import PersonProfile from "../assets/person.svg";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import voiceWaveAnimation from "../assets/voice-wave.json";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import downloadIcon from '../assets/downloadicon.svg';

import SarahBotProfile from "../assets/sarah-bot.png";
import JordanBotProfile from "../assets/jordan-bot.png";

interface UnifiedVoiceComponentProps {
  isMicOn: boolean;
  isPlaying: boolean;
  selectedLanguage: string;
  selectedVoice: string;
  isTranscriptionOn: boolean;
  isSpeechStarted: boolean;
  isShowDownloadButton: boolean;
  isLoading: boolean;
  isMetadataLoading: boolean;
  onMicToggle: () => void;
  onPlayToggle: () => void;
  handleMetadataDownload: () => void;
  handleSummaryDownload: () => void;
  onLanguageChange: (language: string) => void;
  onVoiceChange: (voice: string) => void;
  onTranscriptionToggle: () => void;
}

interface LabelControlProps {
  label: string;
  value: string;
  onClick: () => void;
  icon?: React.ReactNode;
}

const LabelControl: React.FC<LabelControlProps> = ({ label, value, onClick, icon }) => {
  return (
    <button
      onClick={onClick}
      className="inline-flex justify-between items-center rounded-md px-3 py-1 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 hover:bg-gray-50 focus:outline-none"
    >
      <div className="flex items-center">
        <span className="text-gray-500 text-base font-semibold leading-[22px] tracking-[0.25px]">{label}</span>
        <span className="mx-1">:</span>
        <span className="text-base font-semibold leading-[22px] tracking-[0.25px]">{value}</span>
      </div>
      {icon || <ChevronDownIcon className="ml-2 h-4 w-4" />}
    </button>
  );
};

const UnifiedVoiceComponent: React.FC<UnifiedVoiceComponentProps> = ({
  isMicOn,
  isPlaying,
  selectedLanguage,
  selectedVoice,
  isTranscriptionOn,
  onMicToggle,
  onPlayToggle,
  onLanguageChange,
  onVoiceChange,
  onTranscriptionToggle,
  isSpeechStarted,
  handleMetadataDownload,
  handleSummaryDownload,
  isShowDownloadButton,
  isLoading,
  isMetadataLoading
}) => {
  const aiLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const userLottieRef = useRef<LottieRefCurrentProps | null>(null);
  const languages = ["English"];
  const voice = ["Merrisa", "Jordan"];

  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const [isVoiceDropdownOpen, setIsVoiceDropdownOpen] = useState(true);
  const [botProfileImage, setBotProfileImage] = useState(BotProfile);
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState(false);
  const [selectedDownloadOption, setSelectedDownloadOption] = useState("Conversation Transcript");
  const [timer, setTimer] = useState<number>(0);
  const [hasLanguageSelection, setHasLanguageSelection] = useState(false);
  const [hasVoiceSelection, setHasVoiceSelection] = useState(false);
  const [isAiAnimating, setIsAiAnimating] = useState(false);
  const [isUserAnimating, setIsUserAnimating] = useState(false);

  // Add refs for each dropdown
  const languageDropdownRef = useRef<HTMLDivElement>(null);
  const voiceDropdownRef = useRef<HTMLDivElement>(null);
  const downloadDropdownRef = useRef<HTMLDivElement>(null);

  // Update the click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      
      // Don't close if clicking on dropdown options
      if (target.nodeType === 1 && (target as Element).tagName === 'BUTTON') {
        return;
      }

      // Check if click was outside language dropdown
      if (!isDropdownOpen && 
          languageDropdownRef.current && 
          !languageDropdownRef.current.contains(target)) {
        setIsDropdownOpen(true); // Close language dropdown
      }

      // Check if click was outside voice dropdown
      if (!isVoiceDropdownOpen && 
          voiceDropdownRef.current && 
          !voiceDropdownRef.current.contains(target)) {
        setIsVoiceDropdownOpen(true); // Close voice dropdown
      }
    };

    // Add listener if any dropdown is open
    if (!isDropdownOpen || !isVoiceDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, isVoiceDropdownOpen]);

  // Toggle functions
  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isPlaying) { // Disable toggle if playing
      setIsDropdownOpen(!isDropdownOpen);
      setIsVoiceDropdownOpen(true); // Close other dropdowns
      setIsDownloadDropdownOpen(false);
    }
  };

  const toggleVoiceDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isPlaying) { // Disable toggle if playing
      setIsVoiceDropdownOpen(!isVoiceDropdownOpen);
      setIsDropdownOpen(true); // Close other dropdowns
      setIsDownloadDropdownOpen(false);
    }
  };

  const toggleDownloadDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
    setIsDropdownOpen(true); // Close other dropdowns
    setIsVoiceDropdownOpen(true);
  };

  // Handle selection
  const handleLanguageSelect = (language: string) => {
    onLanguageChange(language);
    setIsDropdownOpen(true);
    setHasLanguageSelection(true);
  };

  const handleVoiceSelect = (voice: string) => {
    onVoiceChange(voice);
    setIsVoiceDropdownOpen(true);
    setHasVoiceSelection(true);
  };

  useEffect(() => {
    if (userLottieRef.current) {
      const shouldAnimate = isSpeechStarted && isMicOn;
      setIsUserAnimating(shouldAnimate);

      if (!shouldAnimate) {
        userLottieRef.current.stop();
        userLottieRef.current.goToAndStop(0);
      }
    }
  }, [isSpeechStarted, isMicOn]);

  useEffect(() => {
    if (aiLottieRef.current) {
      const shouldAnimate = isPlaying && !isSpeechStarted;
      setIsAiAnimating(shouldAnimate);

      if (!shouldAnimate) {
        aiLottieRef.current.stop();
        aiLottieRef.current.goToAndStop(0);
      }
    }
  }, [isPlaying, isSpeechStarted]);

  useEffect(() => {
    switch (selectedVoice) {
      case "Merrisa":
        setBotProfileImage(BotProfile);
        break;
      case "Sarah":
        setBotProfileImage(SarahBotProfile);
        break;
      case "Jordan":
        setBotProfileImage(JordanBotProfile);
        break;
      default:
        setBotProfileImage(BotProfile);
    }
  }, [selectedVoice]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (isPlaying) {
      setTimer(0); // Reset timer when starting
      intervalId = setInterval(() => {
        setTimer(prev => prev + 1);
      }, 1000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isPlaying]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className={`flex flex-col h-full  md:mt-[-1rem]  border border-[grey]-100 rounded-2xl mx-0 md:m-0 bg-white   ${isTranscriptionOn ? '-mb-16 ' : ''}`}>
      {/* Header section  mobile view transcription off */}
      <div className="flex items-center justify-between md:px-4 md:py-2 bg-white m-1 md:pl-2 md:pr-2">
        {!isTranscriptionOn ? (
          // Controls when transcription is OFF
          <div className="flex items-center justify-between w-full pr-2 pl-4 md:px-0">
            <div className="flex items-center space-x-4">
              {/* Transcription toggle */}
              <div className="flex items-center space-x-1">
                <div className="text-sm font-medium  
                                md:text-base md:font-semibold md:leading-[22px] md:tracking-[0.25px]">
                  Transcription:
                </div>
                <button
                  onClick={onTranscriptionToggle}
                  className={`w-7 h-4 mt-1 flex items-center rounded-full p-0.5 transition-colors duration-300 
                               md:w-12  md:h-6 flex items-center rounded-full p-1 transition-colors duration-300
                    ${isTranscriptionOn ? "bg-blue-500" : "bg-gray-300"
                  }`}
                >
                  <div
                    className={`h-3 w-3 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                      isTranscriptionOn ? "translate-x-3" : "translate-x-0"
                    }`}
                  ></div>
                </button>
              </div>

              {/* Language dropdown - Different styles for mobile and desktop */}
              <div ref={languageDropdownRef} className="relative">
                <button
                  onClick={toggleDropdown}
                  disabled={isPlaying}
                  className={`inline-flex justify-between items-center rounded-md px-3 py-1 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 bg-gray-50 hover:bg-gray-100 
                    ${isPlaying ? 'cursor-not-allowed opacity-50' : ''}
                    md:inline-flex md:justify-between md:items-center
                    hidden md:flex` // Hide on mobile
                  }
                >
                  <span className="mr-1">Language:</span> {selectedLanguage}
                  <ChevronDownIcon className="ml-2 h-4 w-4" />
                </button>

                {/* Mobile version of language button */}
                <button
                  onClick={toggleDropdown}
                  disabled={isPlaying}
                  className={`md:hidden inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 ml-[-1rem] 
                    ${isPlaying ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  {selectedLanguage}
                  <ChevronDownIcon className="ml-0.5 h-3 w-3" />
                </button>

                {!isDropdownOpen && (
                  <div className="absolute mt-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {languages.map((language) => (
                        <button
                          key={language}
                          onClick={() => handleLanguageSelect(language)}
                          className="block w-full px-4 py-2 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 hover:bg-gray-100 text-left"
                        >
                          {language}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Voice dropdown - Different styles for mobile and desktop */}
              <div ref={voiceDropdownRef} className="relative">
                <button
                  onClick={toggleVoiceDropdown}
                  disabled={isPlaying}
                  className={`inline-flex justify-between items-center rounded-md px-3 py-1 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 bg-gray-50 hover:bg-gray-100 
                    ${isPlaying ? 'cursor-not-allowed opacity-50' : ''}
                    md:inline-flex md:justify-between md:items-center
                    hidden md:flex` // Hide on mobile
                  }
                >
                  <span className="mr-1">Voice Profile:</span> {selectedVoice}
                  <ChevronDownIcon className="ml-2 h-4 w-4" />
                </button>

                {/* Mobile version of voice button */}
                <button
                  onClick={toggleVoiceDropdown}
                  disabled={isPlaying}
                  className={`md:hidden inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium text-gray-700 bg-gray-50 hover:bg-gray-100 ml-[-1rem] 
                    ${isPlaying ? 'cursor-not-allowed opacity-50' : ''}`}
                >
                  {selectedVoice}
                  <ChevronDownIcon className="ml-0.5 h-3 w-3" />
                </button>

                {!isVoiceDropdownOpen && (
                  <div className="absolute mt-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1">
                      {voice.map((v) => (
                        <button
                          key={v}
                          onClick={() => handleVoiceSelect(v)}
                          className="block w-full px-4 py-2 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 hover:bg-gray-100 text-left"
                        >
                          {v}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Download dropdown destop rightside  and also in the mobile view(when the transcription is on) */}
            <div ref={downloadDropdownRef} className="relative">
              <button
                onClick={toggleDownloadDropdown}
                disabled={isPlaying || !isShowDownloadButton}
                className={`inline-flex justify-between items-center rounded-md px-3 py-1 text-base font-semibold leading-[22px] tracking-[0.25px]
                  ${(isPlaying || !isShowDownloadButton)
                    ? 'md:bg-gray-300 md:text-gray-500 cursor-not-allowed'
                    : 'md:text-white md:bg-blue-500 md:hover:bg-blue-600'} 
                  focus:outline-none`}
              >
                {/*   desktop view */}
                <span className="hidden md:inline text-base font-semibold leading-[22px] tracking-[0.25px]">
                  Download
                </span>
                {/*  mobile view */}
                <span className="md:hidden">
                  <img src={downloadIcon} alt="Download" className="w-6 h-6" />
                </span>
                <ChevronDownIcon className=" ml-1 h-4 w-4   hidden md:inline" />
              </button>
              {isDownloadDropdownOpen && !isPlaying && isShowDownloadButton && (
                <div className="absolute right-0 w-[170px] rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="px-4 py-2 text-sm font-semibold leading-[22px] tracking-[0.25px] text-gray-700">
                    Choose the Format
                  </div>
                  <div className="px-4 py-2">
                    <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                      <input
                        type="radio"
                        name="downloadOption"
                        value="Conversation Transcript"
                        checked={selectedDownloadOption === "Conversation Transcript"}
                        onChange={() => setSelectedDownloadOption("Conversation Transcript")}
                        className="form-radio text-blue-500"
                      />
                      <span className="text-base font-semibold leading-[22px] tracking-[0.25px]">
                        Conversation Transcript
                      </span>
                    </label>
                    <label className="flex items-center space-x-2 mt-2 cursor-pointer hover:bg-gray-50 p-2 rounded ">
                      <input
                        type="radio"
                        name="downloadOption"
                        value="Data Extract"
                        checked={selectedDownloadOption === "Data Extract"}
                        onChange={() => setSelectedDownloadOption("Data Extract")}
                        className="form-radio text-blue-500"
                      />
                      <span className="text-base font-semibold leading-[22px] tracking-[0.25px] ">
                        Data Extract
                      </span>
                    </label>
                  </div>
                  <div className="px-4 py-2 ">
                    <button
                      onClick={() => {
                        if (selectedDownloadOption === "Conversation Transcript") {
                        handleMetadataDownload();
                        } else {
                          handleSummaryDownload();
                        }
                        setIsDownloadDropdownOpen(false);
                      }}
                      disabled={isPlaying || !isShowDownloadButton}
                      className={`w-full py-2 px-4 text-sm font-medium text-center rounded-lg
                        ${(isPlaying || !isShowDownloadButton)
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'text-white bg-blue-500 hover:bg-blue-600'}`}
                    >
                      {isMetadataLoading || isLoading ? 'Downloading...' : 'Download'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : (
          // Controls when transcription is ON in desktop view
          <div className="hidden md:flex items-center space-x-4">
            {/* Language dropdown */}
            <div ref={languageDropdownRef} className="relative">
              <button
                  onClick={toggleDropdown}
                  disabled={isPlaying} // Disable when playing
                className={`inline-flex justify-between items-center rounded-md px-3 py-1 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 bg-gray-50 hover:bg-gray-100 ${
                  isPlaying ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                <span className="mr-1">Language:</span> {selectedLanguage}
                <ChevronDownIcon className="ml-1 h-4 w-4" />
              </button>
              {!isDropdownOpen && (
                <div className="absolute left-0 mt-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    {languages.map((language) => (
                      <button
                        key={language}
                        onClick={() => handleLanguageSelect(language)}
                        className="block w-full px-4 py-2 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 hover:bg-gray-100 text-left"
                      >
                        {language}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Voice dropdown */}
            <div ref={voiceDropdownRef} className="relative">
              <button
                  onClick={toggleVoiceDropdown}
                  disabled={isPlaying} // Disable when playing
                className={`inline-flex justify-between items-center rounded-md px-3 py-1 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 bg-gray-50 hover:bg-gray-100 ${
                  isPlaying ? 'cursor-not-allowed opacity-50' : ''
                }`}
              >
                <span className="mr-1">Voice Profile:</span> {selectedVoice}
                <ChevronDownIcon className="ml-2 h-4 w-4" />
              </button>
              {!isVoiceDropdownOpen && (
                <div className="absolute left-0 mt-1 w-40 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    {voice.map((voice) => (
                      <button
                        key={voice}
                        onClick={() => handleVoiceSelect(voice)}
                        className="block w-full px-4 py-2 text-base font-semibold leading-[22px] tracking-[0.25px] text-gray-700 hover:bg-gray-100 text-left"
                      >
                        {voice}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Voice profiles section */}
      <div className={`flex-grow ${isTranscriptionOn ? "mt-0" : "mt-2"} md:mt-4 flex flex-col items-center 
                      ${isTranscriptionOn ? "justify-center" : "justify-center"} bg-white px-0 md:px-4
                      ${isTranscriptionOn ? "-mb-10" : "mb-4"} md:mb-0 h-full`}>
        {/* This is the transcription on view  so chnage this to adjust the size of the box above the chatcomponent*/}
        {isTranscriptionOn ? (
          <>
            {/* Desktop view - when the transcription is on */}
            <div className="hidden md:flex flex-col items-center">
              {/* <p className="text-[23px] font-bold text-gray-900 mb-3 uppercase tracking-wide">
              {localStorage.getItem("healthplan_name")}
            </p> */}
              {/* Voice profiles container - Now only showing AI profile */}
              <div className="flex flex-row justify-center w-full mb-8">
                {/* Bot Profile - With animation wrapping around the image */}
                <div className="w-[150px] h-[150px] relative flex items-center justify-center">
                  <div className="absolute w-[200px] h-[200px]">
                    <Lottie
                      animationData={voiceWaveAnimation}
                      lottieRef={aiLottieRef}
                      loop={isAiAnimating}
                      autoPlay={false}
                      style={{ display: isAiAnimating ? 'block' : 'none' }}
                      className="w-full h-full"
                      onComplete={() => {
                        if (!isAiAnimating && aiLottieRef.current) {
                          aiLottieRef.current.goToAndStop(0);
                        }
                      }}
                    />
                  </div>
                  <div className="z-10 w-[130px] h-[130px] bg-white rounded-full shadow-md flex items-center justify-center">
                    <img
                      src={botProfileImage}
                      alt="Bot Profile"
                      className="w-[130px] h-[130px] rounded-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/*  Bot name text in desktop view when the transcription is on */}
              <div className="text-center mt-2">
                <p className="text-xxl font-medium text-gray-600">
                  Hello! My name is
                </p>
                <p className="text-2xl font-semibold text-gray-600 mt-1">
                  {selectedVoice}
                </p>
              </div>


              {/* Timer - Increased size */}
              <div className="text-3xl font-semibold text-gray-700 mb-6 mt-4">
                {formatTime(timer)}
              </div>
              {isPlaying && (
                <p className="text-[16px] font-medium tracking-wide text-gray-700 mb-6 leading-[24px] text-center">
                  You're now connected with {localStorage.getItem("first_name")}.
                </p>
              )}


              {/* Control buttons */}
              <div className="flex justify-center space-x-8">
                <button
                  onClick={onMicToggle}
                  className="flex items-center justify-center p-4 bg-blue-600 rounded-full"
                >
                  <img
                    src={isMicOn ? micOnIcon : micOffIcon}
                    alt="Mic Toggle"
                    className="w-5 h-5"
                  />
                </button>

                <button
                  onClick={onPlayToggle}
                  className={`flex items-center justify-center p-4 rounded-full ${isPlaying ? "bg-red-500" : "bg-green-500"}`}
                >
                  <img
                    src={isPlaying ? callDropIcon : callIcon}
                    alt="Play/Stop Toggle"
                    className="w-5 h-5"
                  />
                </button>
              </div>
            </div>

            {/* Mobile view - shown only on mobile */}
            <div className="md:hidden flex flex-col items-center  mb-16  w-full">
              {/* Add mobile controls when transcription is ON */}
              {isTranscriptionOn && (
                <div className="w-full pr-4 pl-8 mb-4 mt-8">
                  <div className="flex items-center justify-between mb-4 ml-[-1rem]">
                    {/* Left side controls */}
                    <div className="flex items-center flex-1 min-w-0 space-x-1">
                      {/* Transcription group */}
                      <div className="flex items-center space-x-1 ml-1  mb-1.5">
                        <div className="text-sm font-medium">
                          Transcription:
                        </div>
                        <button
                          onClick={onTranscriptionToggle}
                          className={`w-7 sm:w-7 h-4 sm:h-4 mt-1 flex items-center rounded-full p-0.5 transition-colors duration-300 ${
                            isTranscriptionOn ? "bg-blue-500" : "bg-gray-300"
                          }`}
                        >
                          <div
                            className={`h-2 sm:h-3 w-2 sm:w-3 bg-white rounded-full shadow-md transform transition-transform duration-300 ${
                              isTranscriptionOn ? "translate-x-3" : "translate-x-0"
                            }`}
                          ></div>
                        </button>
                      </div>

                      {/* Language dropdown - Mobile version */}
                      <div ref={languageDropdownRef} className="relative ml-1 mb-1.5">
                        <button
                          onClick={toggleDropdown}
                          disabled={isPlaying}
                          className={`inline-flex justify-between items-center rounded-md pr-3 pl-1 py-1 text-sm font-medium leading-[22px] tracking-[0.25px] text-gray-700 bg-gray-50 hover:bg-gray-100 
                           ${isPlaying ? 'cursor-not-allowed opacity-50' : ''}`}
                        >
                          {selectedLanguage}
                          <ChevronDownIcon className="ml-0.5 h-2 w-2 sm:h-3 sm:w-3" />
                        </button>
                        {!isDropdownOpen && (
                          <div className="absolute mt-1 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                            <div className="py-1">
                              {languages.map((language) => (
                                <button
                                  key={language}
                                  onClick={() => handleLanguageSelect(language)}
                                  className="block w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 text-left"
                                >
                                  {language}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Voice dropdown - Mobile version */}
                    <div ref={voiceDropdownRef} className="relative ml-1 mb-1.5">
                      <button
                        onClick={toggleVoiceDropdown}
                        disabled={isPlaying} 
                        className={`inline-flex justify-between items-center rounded-md px-3 py-1 text-sm font-medium leading-[22px] tracking-[0.25px] text-gray-700 bg-gray-50 hover:bg-gray-100 ml-[-1rem] 
                            ${isPlaying ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        {selectedVoice}
                        <ChevronDownIcon className="ml-0.5 h-2 w-2 sm:h-3 sm:w-3" />
                      </button>
                      {!isVoiceDropdownOpen && (
                        <div className="absolute mt-1 w-32 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                          <div className="py-1">
                            {voice.map((v) => (
                              <button
                                key={v}
                                onClick={() => handleVoiceSelect(v)}
                                className="block w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 text-left"
                              >
                                {v}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    </div>

                    {/* download button for the mobile view when the transcription is on */}
                    <div ref={downloadDropdownRef} className="relative shrink-0 ">
                      <button
                        onClick={toggleDownloadDropdown}
                        disabled={isPlaying || !isShowDownloadButton}
                        className={`inline-flex justify-between items-center rounded-md px-2 sm:px-3 py-1 
                          ${(isPlaying || !isShowDownloadButton)
                            ? 'cursor-not-allowed'
                            : 'text-white'} 
                          focus:outline-none`}
                      >
                        
                        <span className="md:hidden">
                          <img src={downloadIcon} alt="Download" className="w-6 h-6 sm:w-6 sm:h-6" />
                        </span>
                      </button>
                      {isDownloadDropdownOpen && !isPlaying && isShowDownloadButton && (
                        <div className="absolute right-0 w-[170px] rounded-xl shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                          <div className="px-4 py-2 text-sm font-semibold leading-[22px] tracking-[0.25px] text-gray-700">
                            Choose the Format
                          </div>
                          <div className="px-4 py-2">
                            <label className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                              <input
                                type="radio"
                                name="downloadOption"
                                value="Conversation Transcript"
                                checked={selectedDownloadOption === "Conversation Transcript"}
                                onChange={() => setSelectedDownloadOption("Conversation Transcript")}
                                className="form-radio text-blue-500"
                              />
                              <span className="text-base font-semibold leading-[22px] tracking-[0.25px]">
                                Conversation Transcript
                              </span>
                            </label>
                            <label className="flex items-center space-x-2 mt-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                              <input
                                type="radio"
                                name="downloadOption"
                                value="Data Extract"
                                checked={selectedDownloadOption === "Data Extract"}
                                onChange={() => setSelectedDownloadOption("Data Extract")}
                                className="form-radio text-blue-500"
                              />
                              <span className="text-base font-semibold leading-[22px] tracking-[0.25px] ">
                                Data Extract
                              </span>
                            </label>
                          </div>
                          <div className="px-4 py-2 ">
                            <button
                              onClick={() => {
                                if (selectedDownloadOption === "Conversation Transcript") {
                                handleMetadataDownload();
                                } else {
                                  handleSummaryDownload();
                                }
                                setIsDownloadDropdownOpen(false);
                              }}
                              disabled={isPlaying || !isShowDownloadButton}
                              className={`w-full py-2 px-4 text-sm font-medium text-center rounded-lg
                                ${(isPlaying || !isShowDownloadButton)
                                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                  : 'text-white bg-blue-500 hover:bg-blue-600'}`}
                            >
                              {isMetadataLoading || isLoading ? 'Downloading...' : 'Download'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Voice profiles container - IN mobile UI when transcription is ON */}
              {isTranscriptionOn ? (
                // Single AI profile for transcription ON
                <div className="flex justify-center w-full mb-0 mt-[-2rem] ">
                  <div className="w-[150px] h-[150px] relative flex items-center justify-center">
                    <div className="absolute w-[150px] h-[150px]">
                      <Lottie
                        animationData={voiceWaveAnimation}
                        lottieRef={aiLottieRef}
                        loop={isAiAnimating}
                        autoPlay={false}
                        style={{ display: isAiAnimating ? 'block' : 'none' }}
                        className="w-full h-full"
                        onComplete={() => {
                          if (!isAiAnimating && aiLottieRef.current) {
                            aiLottieRef.current.goToAndStop(0);
                          }
                        }}
                      />
                    </div>
                    <div className="z-10 w-[100px] h-[100px] bg-white rounded-full shadow-md flex items-center justify-center">
                      <img
                        src={botProfileImage}
                        alt="Bot Profile"
                        className="w-[100px] h-[100px] rounded-full object-cover"
                      />
                    </div>
                  </div>
                </div>
              ) : (
                // Existing dual profile view for transcription OFF
                <div className="flex flex-row justify-center w-full space-x-4 mb-1">
                  {/* Existing Bot and User Profile code for transcription OFF */}
                  {/* ... existing code ... */}
                </div>
              )}

              {/*  text when transcription is ON mobile view */}
              {isTranscriptionOn && (
                <div className="text-center mb-4">
                <p className="text-base text-gray-600">Hello! My name is</p>
                <p className="text-xl font-semibold text-gray-700">{selectedVoice}</p>
              </div>
              )}
            </div>
          </>
        ) : (


          // this is the desktop view when transcription is off
          <>
            {/* Desktop view - hidden on mobile */}
            <div className="hidden md:flex flex-col items-center mt-10">


              {/* Single centered bot profile */}
              <div className="flex flex-row justify-center w-full mb-8">
                <div className="w-[150px] h-[150px] relative flex items-center justify-center">
                  <div className="absolute w-[250px] h-[250px]">
                    <Lottie
                      animationData={voiceWaveAnimation}
                      lottieRef={aiLottieRef}
                      loop={isAiAnimating}
                      autoPlay={false}
                      style={{ display: isAiAnimating ? 'block' : 'none' }}
                      className="w-full h-full"
                      onComplete={() => {
                        if (!isAiAnimating && aiLottieRef.current) {
                          aiLottieRef.current.goToAndStop(0);
                        }
                      }}
                    />
                  </div>
                  <div className="z-10 w-[130px] h-[130px] bg-white rounded-full shadow-md flex items-center justify-center">
                    <img
                      src={botProfileImage}
                      alt="Bot Profile"
                      className="w-[130px] h-[130px] rounded-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Bot name text  when the transcription is off desktop view*/}
              <div className="text-center mt-5">
                <p className="text-xxl font-medium text-gray-600">
                  Hello! My name is
                </p>
                <p className="text-2xl font-semibold text-gray-600 mt-1">
                  {selectedVoice}
                </p>
              </div>

              {/* Timer */}
              <div className="text-3xl font-semibold text-gray-700 mb-6 mt-4">
                {formatTime(timer)}
              </div>

              {/* Connected with text */}
              {isPlaying && (
                <p className="text-[16px] font-medium tracking-wide text-gray-700 mb-6 leading-[24px]text-center">
                  You're now connected with {localStorage.getItem("first_name")}.
                </p>
              )}

              {/* Control buttons */}
              <div className="flex justify-center space-x-8">
                <button
                  onClick={onMicToggle}
                  className="flex items-center justify-center p-4 bg-blue-600 rounded-full"
                >
                  <img
                    src={isMicOn ? micOnIcon : micOffIcon}
                    alt="Mic Toggle"
                    className="w-5 h-5"
                  />
                </button>

                <button
                  onClick={onPlayToggle}
                  className={`flex items-center justify-center p-4 rounded-full ${isPlaying ? "bg-red-500" : "bg-green-500"}`}
                >
                  <img
                    src={isPlaying ? callDropIcon : callIcon}
                    alt="Play/Stop Toggle"
                    className="w-5 h-5"
                  />
                </button>
              </div>
            </div>

            {/* Mobile view */}
            <div className="md:hidden flex flex-col w-full">
              {!isTranscriptionOn && (
                // Mobile view when transcription is OFF
                // <div className="flex flex-col items-center justify-center min-h-[calc(100vh-400px)]">
                <div className="flex flex-col items-center justify-center min-h-[calc(100vh-200px)] sm:min-h-[calc(100vh-300px)] lg:min-h-[calc(100vh-400px)]">

                  {/* Single centered bot profile */}
                  <div className="w-[150px] h-[150px] relative flex items-center justify-center mb-4">
                    <div className="absolute w-[200px] h-[200px]">
                      <Lottie
                        animationData={voiceWaveAnimation}
                        lottieRef={aiLottieRef}
                        loop={isAiAnimating}
                        autoPlay={false}
                        style={{ display: isAiAnimating ? 'block' : 'none' }}
                        className="w-full h-full"
                        onComplete={() => {
                          if (!isAiAnimating && aiLottieRef.current) {
                            aiLottieRef.current.goToAndStop(0);
                          }
                        }}
                      />
                    </div>
                    <div className="z-10 w-[130px] h-[130px] bg-white rounded-full shadow-md flex items-center justify-center">
                      <img
                        src={botProfileImage}
                        alt="Bot Profile"
                        className="w-[130px] h-[130px] rounded-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Bot name and greeting */}
                  <div className="text-center mb-4">
                    <p className="text-base text-gray-600">Hello! My name is</p>
                    <p className="text-xl font-semibold text-gray-700">{selectedVoice}</p>
                  </div>
                  </div>
              )}
            </div>
          </>
        )}

        {/* Mobile Controls mobile UI */}
        <div className="md:hidden fixed bottom-0 left-0 right-0">
          <div className="bg-white py-4 shadow-lg">
            {/* Add connected text above timer when playing */}
            {isPlaying && (
              <p className="text-[18px] font-medium tracking-wide text-gray-700 mb-4 text-center">
                You're now connected with {localStorage.getItem("first_name")}.
              </p>
            )}
            <div className="flex justify-center mb-3">
              <div className="text-2xl font-semibold text-gray-700">
                {formatTime(timer)}
              </div>
            </div>
            <div className="flex justify-center space-x-6">
              <button
                onClick={onMicToggle}
                className="flex items-center justify-center p-3 bg-blue-600 rounded-full"
              >
                <img
                  src={isMicOn ? micOnIcon : micOffIcon}
                  alt="Mic Toggle"
                  className="w-6 h-6"
                />
              </button>

              <button
                onClick={onPlayToggle}
                className={`flex items-center justify-center p-3 rounded-full ${isPlaying ? "bg-red-500" : "bg-green-500"
                  }`}
              >
                <img
                  src={isPlaying ? callDropIcon : callIcon}
                  alt="Play/Stop Toggle"
                  className="w-6 h-6"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop Powered by Wizr.ai - hidden on mobile */}
      <div className="hidden md:flex justify-end py-4 px-10 text-right font-montserrat text-xs font-medium leading-4 tracking-[0.4px] underline decoration-slice">
        Powered by Wizr.ai
      </div>
    </div>
  );
};

export default UnifiedVoiceComponent;
